import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, CheckCircle, RefreshCw, Smartphone, BarChart3, ShieldCheck, Utensils, Heart } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroBackground from "@/assets/hero-background.jpg";
import claraPortrait from "@/assets/clara-portrait-final.png";

const metodologia = [
  { step: "1", title: "Entendo sua rotina", desc: "Horários de trabalho, estudo e treino, compras e cozinha: tudo entra no planejamento, porque o plano precisa caber na sua vida." },
  { step: "2", title: "Monto um plano alimentar personalizado", desc: "Com produtos dos mercados australianos (Coles, Woolworths, Aldi), respeitando seus gostos, seu orçamento e seus objetivos." },
  { step: "3", title: "Acompanho de perto sua evolução", desc: "Consultas regulares, análise de exames e ajustes no plano sempre que necessário. Você não fica sem apoio em nenhuma etapa." },
  { step: "4", title: "Suporte contínuo entre as consultas", desc: "Acesso à plataforma e contato direto comigo por WhatsApp. Ficou com dúvida no mercado? É só me mandar uma foto do produto." },
];

const suporteItems = [
  { icon: Smartphone, title: "WhatsApp direto comigo", desc: "Dúvidas, ajustes rápidos, fotos de rótulos. Você fala direto comigo, sem intermediários." },
  { icon: RefreshCw, title: "Ajustes constantes no plano", desc: "Mudou a rotina? Viajou? Começou a treinar? Eu adapto o plano sem esperar a próxima consulta." },
  { icon: BarChart3, title: "Acompanhamento de exames", desc: "Analiso seus exames e acompanho sua evolução para conferir se está tudo no caminho certo." },
  { icon: ShieldCheck, title: "Plataforma online", desc: "Conteúdo de apoio para ajudar você a manter a constância no dia a dia." },
];

const exemplosRotina = [
  { perfil: "Trabalha full-time e estuda à noite", rotina: "Meal prep no domingo, almoços rápidos para o trabalho e jantar prático depois da aula. Tudo com lista de compras do Woolworths." },
  { perfil: "Mãe com filhos pequenos", rotina: "Refeições para toda a família, lanches saudáveis para as crianças e praticidade para quem não tem tempo de cozinhar todo dia." },
  { perfil: "Treina e quer ganhar massa", rotina: "Plano com macros ajustados ao seu treino, horário das refeições e opções práticas para levar para a academia." },
  { perfil: "Acabou de chegar à Austrália", rotina: "Guia de substituição de produtos brasileiros, lista de compras para quem está começando e receitas simples e acessíveis." },
];

export const faqItems = [
  { question: "Com que frequência são as consultas?", answer: "Varia de acordo com seus objetivos e o momento do acompanhamento. No início, os encontros costumam ser mais frequentes; depois, espaçamos conforme você ganha autonomia." },
  { question: "Posso cancelar quando quiser?", answer: "Sim, sem multa e sem burocracia. É só me avisar." },
  { question: "O plano é feito com produtos daqui da Austrália?", answer: "Sim. Uso produtos que você encontra no Coles, no Woolworths e no Aldi, sem depender de ingredientes que só existem no Brasil." },
  { question: "E se eu não souber cozinhar?", answer: "Sem problema. O plano inclui opções simples e práticas, e pelo WhatsApp eu passo dicas e alternativas rápidas." },
  { question: "Quanto tempo leva para ver resultados?", answer: "Os acompanhamentos normalmente têm um período mínimo de 3 meses para ver resultados consistentes, mas isso depende de cada pessoa e de cada plano. Muitos pacientes relatam, já nas primeiras semanas, mais disposição e uma relação mais tranquila com a comida." },
];

const CTAButtons = ({ openDialog }: { openDialog: () => void }) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
    <Button
      size="lg"
      variant="gradient"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      style={{ background: 'var(--gradient-green)' }}
      onClick={openDialog}
    >
      <Calendar className="mr-2 h-5 w-5" />
      Agendar consulta
    </Button>
    <Button
      size="lg"
      variant="gradient-outline"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gradient-green)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
      onClick={() => window.open('https://wa.me/61423038977', '_blank')}
    >
      <MessageCircle className="mr-2 h-5 w-5" />
      Falar no WhatsApp
    </Button>
  </div>
);

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <>

      <div className="min-h-screen">
        <Navigation />

        <main>
          {/* HERO */}
          <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
            <div
              className="absolute inset-0 opacity-15"
              style={{
                backgroundImage: `url(${heroBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
              <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                Clara Camille · Nutricionista
              </p>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">
                Acompanhamento nutricional na Austrália para rotina real
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">
                Plano alimentar personalizado, suporte entre as consultas e ajustes conforme a sua rotina muda. Atendimento em português, presencial em Brisbane ou online em toda a Austrália.
              </p>
              <CTAButtons openDialog={openDialog} />
            </div>
          </section>

          {/* METODOLOGIA */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Minha metodologia
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Como funciona o acompanhamento nutricional
                </h2>
              </div>

              <div className="space-y-6 sm:space-y-8">
                {metodologia.map((item) => (
                  <div key={item.step} className="flex gap-4 sm:gap-6 items-start">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-typewriter font-bold text-lg sm:text-xl text-primary-foreground" style={{ background: 'var(--gradient-green)' }}>
                      {item.step}
                    </div>
                    <div className="space-y-1 sm:space-y-2 pt-1">
                      <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* SUPORTE */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Suporte contínuo
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  O que você recebe entre as consultas
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
                {suporteItems.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex gap-4 sm:gap-5 p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-primary/5 hover:bg-primary/10 transition-colors">
                      <div className="flex-shrink-0">
                        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-primary/15 flex items-center justify-center">
                          <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-primary" strokeWidth={1.5} />
                        </div>
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* EXEMPLOS DE ROTINA */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Na prática
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Exemplos de como adapto o plano à sua rotina
                </h2>
              </div>

              <div className="space-y-4 sm:space-y-6">
                {exemplosRotina.map((item, index) => (
                  <div key={index} className="p-5 sm:p-6 rounded-xl sm:rounded-2xl bg-primary/5">
                    <h3 className="text-base sm:text-lg font-bold text-foreground font-sans mb-2">
                      <span className="gradient-text italic">{item.perfil}</span>
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.rotina}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* QUEM TE ATENDE */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10 p-5 sm:p-8 rounded-2xl sm:rounded-3xl bg-primary/5">
                <div className="flex-shrink-0">
                  <img
                    src={claraPortrait}
                    alt="Clara Camille, nutricionista brasileira na Austrália"
                    className="w-28 h-28 sm:w-36 sm:h-36 rounded-full object-cover object-top shadow-lg"
                  />
                </div>
                <div className="text-center sm:text-left space-y-2 sm:space-y-3">
                  <h3 className="text-xl sm:text-2xl font-typewriter font-bold text-foreground">Clara Camille</h3>
                  <p className="text-sm sm:text-base text-primary font-typewriter font-semibold">Nutricionista · Brisbane, Austrália</p>
                  <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                    Brasileira, nutricionista e imigrante. Entendo sua rotina porque vivo a mesma realidade. Atendo presencialmente em Brisbane e online para toda a Austrália.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Dúvidas frequentes
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Perguntas sobre o acompanhamento
                </h2>
              </div>

              <Accordion type="single" collapsible className="space-y-3">
                {faqItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="border border-border rounded-xl px-5 sm:px-6 bg-card"
                  >
                    <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-semibold text-foreground hover:no-underline py-4 sm:py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <div className="mt-10 text-center">
                <p className="text-sm text-muted-foreground font-sans">
                  Conheça mais sobre meu trabalho na página{" "}
                  <Link to="/nutricionista-brasileira-na-australia" className="gradient-text italic font-semibold hover:opacity-80 transition-opacity">
                    Nutricionista brasileira na Austrália
                  </Link>.
                </p>
              </div>
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
            <div
              className="absolute inset-0 blur-sm scale-105"
              style={{
                backgroundImage: `url(${heroBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-0 opacity-80" style={{ background: 'var(--gradient-green)' }} />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 flex justify-center">
              <div
                className="max-w-2xl w-full text-center space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl px-5 py-10 sm:px-8 sm:py-12 lg:px-14 lg:py-16"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(24px) saturate(1.4)',
                  WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-typewriter font-bold text-white leading-tight">
                  Comece seu acompanhamento
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 font-sans leading-relaxed">
                  Na consulta inicial, conversamos sobre seus objetivos e sua rotina, e eu explico como posso ajudar você a cuidar da alimentação na Austrália.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto"
                    onClick={openDialog}
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Agendar consulta
                  </Button>
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto"
                    style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(12px)', color: 'white' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                    onClick={() => window.open('https://wa.me/61423038977', '_blank')}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Falar no WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const AcompanhamentoNutricional = () => (
  <ScheduleProvider>
    <PageContent />
  </ScheduleProvider>
);

export default AcompanhamentoNutricional;
